import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { localizedPath, checkMediaAttachments, MEDIA_ATTACH_ACCEPT } from '@/lib/contactApi';

interface FormData {
  name: string;
  furigana: string;
  email: string;
  phone: string;
  category: string;
  content: string;
  privacyAgreed: boolean;
}

const initialFormData: FormData = {
  name: '',
  furigana: '',
  email: '',
  phone: '',
  category: '',
  content: '',
  privacyAgreed: false,
};

export default function ContactForm() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // 確認画面の「修正する」で戻ってきたときは、入力内容と添付をそのまま復元する
  const location = useLocation();
  const back = (location.state || {}) as { formData?: FormData; files?: File[] };
  const [formData, setFormData] = useState<FormData>(back.formData ?? initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // 取材・メディア関連のときだけ資料の添付を受け付ける(確認画面へ File のまま渡し、送信時に base64 化する)。
  const [files, setFiles] = useState<File[]>(Array.isArray(back.files) ? back.files : []);
  // 取り込んだら、この履歴エントリの state を消す。残すと送信完了後にブラウザの「戻る」で
  // 送信済みの入力と添付が入った画面に戻り、そのまま送ると同じ問い合わせが2回届く。
  // 入力と添付は上の useState に取り込み済みなので、画面からは消えない。
  useEffect(() => {
    if (location.state) {
      navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state: null });
    }
  }, [location, navigate]);
  const isMedia = formData.category === 'media';

  const categories = [
    { value: 'course', label: t('contact.form.cat.course') },
    { value: 'pricing', label: t('contact.form.cat.pricing') },
    { value: 'admission', label: t('contact.form.cat.admission') },
    { value: 'seminar', label: t('contact.form.cat.seminar') },
    { value: 'corporate', label: t('contact.form.cat.corporate') },
    { value: 'media', label: t('contact.form.cat.media') },
    { value: 'other', label: t('contact.form.cat.other') },
  ];

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = t('contact.form.nameError');
    if (!formData.furigana.trim()) newErrors.furigana = t('contact.form.furiganaError');
    if (!formData.email.trim()) {
      newErrors.email = t('contact.form.emailError');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.form.emailInvalidError');
    }
    if (!formData.category) newErrors.category = t('contact.form.categoryError');
    if (!formData.content.trim()) {
      newErrors.content = t('contact.form.contentError');
    } else if (formData.content.length > 500) {
      newErrors.content = t('contact.form.contentLengthError');
    }
    if (!formData.privacyAgreed) {
      newErrors.privacyAgreed = t('contact.form.privacyError');
    }
    const attachProblem = isMedia ? checkMediaAttachments(files) : '';
    if (attachProblem) newErrors.attachments = attachProblem;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      navigate(localizedPath('/contact/confirm'), { state: { formData, files: isMedia ? files : [] } });
    }
  };

  const updateField = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <section className="relative py-20 md:py-28 bg-background-100/40" id="contact-form">
      <div className="max-w-[700px] mx-auto px-8">
        <div className="text-center mb-14">
          <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">
            {t('contact.form.label')}
          </p>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('contact.form.title')}
          </h2>
          <p className="text-accent-500 text-[15px] tracking-[0.12em] mb-4 font-label">
            {t('contact.form.subtitleEn')}
          </p>
          <div className="gold-line mx-auto mb-5"></div>
          <p className="text-[15px] text-foreground-600 tracking-[0.04em] leading-relaxed">
            {t('contact.form.lead')}
          </p>
        </div>

        {/* Alternative contact */}
        <div className="text-center mb-10 bg-background-50 border border-background-200/60 p-5">
          <p className="text-[14px] text-foreground-600 tracking-[0.04em] leading-relaxed">
            {t('contact.form.altContact')}{' '}
            <a href="tel:0368244086" className="text-foreground-800 font-medium hover:text-accent-600 transition-colors">
              03-6824-4086
            </a>
            {' '}{t('contact.form.altOr')}{' '}
            <a href="mailto:info@butler-academy.jp" className="text-accent-600 hover:text-accent-500 transition-colors">
              info@butler-academy.jp
            </a>
            {t('contact.form.altAccept')}
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-7">
            {/* Name */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('contact.form.name')} <span className="text-accent-600 text-[12px]">{t('contact.form.required')}</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('contact.form.namePlaceholder')}
              />
              {errors.name && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.name}</p>
              )}
            </div>

            {/* Furigana */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('contact.form.furigana')} <span className="text-accent-600 text-[12px]">{t('contact.form.required')}</span>
              </label>
              <input
                type="text"
                name="furigana"
                value={formData.furigana}
                onChange={(e) => updateField('furigana', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('contact.form.furiganaPlaceholder')}
              />
              {errors.furigana && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.furigana}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('contact.form.email')} <span className="text-accent-600 text-[12px]">{t('contact.form.required')}</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('contact.form.emailPlaceholder')}
              />
              {errors.email && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('contact.form.phone')} <span className="text-foreground-400 text-[12px]">{t('contact.form.optional')}</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400"
                placeholder={t('contact.form.phonePlaceholder')}
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('contact.form.category')} <span className="text-accent-600 text-[12px]">{t('contact.form.required')}</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={(e) => updateField('category', e.target.value)}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 16px center',
                }}
              >
                <option value="">{t('contact.form.categoryPlaceholder')}</option>
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.category}</p>
              )}
              {formData.category === 'corporate' && (
                <div className="mt-3 p-4 bg-background-50 border border-background-200/60">
                  <p className="text-[14px] text-foreground-600 tracking-[0.04em] leading-relaxed mb-2">
                    {t('contact.form.cat.corporate')}
                  </p>
                  <Link
                    to="/corporate"
                    className="inline-flex items-center gap-2 text-[14px] text-accent-600 hover:text-accent-500 transition-colors tracking-[0.04em]"
                  >
                    {t('contact.related.3.title')}
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </Link>
                </div>
              )}
            </div>

            {/* Attachments (media only) */}
            {isMedia && (
              <div>
                <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                  {t('contact.form.attach')} <span className="text-foreground-400 text-[12px]">{t('contact.form.optional')}</span>
                </label>
                <input
                  type="file"
                  name="attachments"
                  multiple
                  accept={MEDIA_ATTACH_ACCEPT}
                  onChange={(e) => {
                    setFiles(Array.from(e.target.files ?? []));
                    if (errors.attachments) setErrors((prev) => { const next = { ...prev }; delete next.attachments; return next; });
                  }}
                  className="w-full text-[15px] text-foreground-700 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-background-100 file:text-foreground-800 hover:file:bg-background-200"
                />
                <p className="text-[13px] text-foreground-500 mt-1.5 tracking-[0.03em] leading-relaxed">{t('contact.form.attachNote')}</p>
                {files.length > 0 && (
                  <p className="text-[13px] text-foreground-700 mt-1.5 tracking-[0.03em] leading-relaxed">
                    {t('contact.form.attachSelected')}: {files.map((f) => f.name).join(' / ')}
                  </p>
                )}
                {errors.attachments && (
                  <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.attachments}</p>
                )}
              </div>
            )}

            {/* Message */}
            <div>
              <label className="block text-[14px] text-foreground-700 tracking-[0.06em] mb-2 font-label">
                {t('contact.form.content')} <span className="text-accent-600 text-[12px]">{t('contact.form.required')}</span>
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={(e) => updateField('content', e.target.value)}
                rows={5}
                maxLength={500}
                className="w-full px-4 py-3 text-[16px] text-foreground-800 bg-background-50 border border-background-200/80 focus:border-accent-500/50 focus:outline-none transition-colors duration-200 placeholder:text-foreground-400 resize-none"
                placeholder={t('contact.form.contentPlaceholder')}
              ></textarea>
              <p className="text-[13px] text-foreground-400 mt-1 text-right">
                {formData.content.length}/500
              </p>
              {errors.content && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.content}</p>
              )}
            </div>

            {/* Privacy */}
            <div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="privacyAgreed"
                  checked={formData.privacyAgreed}
                  onChange={(e) => updateField('privacyAgreed', e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 border-2 transition-colors duration-200 ${
                    formData.privacyAgreed
                      ? 'bg-accent-500 border-accent-500'
                      : 'border-background-300 bg-background-50'
                  }`}
                >
                  {formData.privacyAgreed && (
                    <i className="ri-check-line text-[12px] text-primary-900"></i>
                  )}
                </div>
                <span className="text-[14px] text-foreground-600 tracking-[0.04em] leading-relaxed">
                  {t('contact.form.privacyAgree')}{' '}
                  <Link to="/privacy" className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors">
                    {t('contact.form.privacy')}
                  </Link>
                  <span className="text-accent-600 text-[12px] ml-1">{t('contact.form.required')}</span>
                </span>
              </label>
              {errors.privacyAgreed && (
                <p className="text-[13px] text-red-500 mt-1.5 tracking-[0.03em]">{errors.privacyAgreed}</p>
              )}
            </div>

            {/* Submit */}
            <div className="pt-4">
              <button
                type="submit"
                className="btn-glow w-full py-3.5 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {t('contact.form.submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
